/**
 * Governance and credits for /sobre. The 11 members are the formal
 * órgãos sociais of the association (Direção · Conselho Fiscal ·
 * Mesa da Assembleia) — they are legal roles, not contributors.
 * Volunteer / contributor credits live in COLOPHON below.
 */

export type GovernanceBody =
  | 'direcao'
  | 'conselho-fiscal'
  | 'mesa-da-assembleia';

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  body: GovernanceBody;
}

export interface GovernanceBodyMeta {
  id: GovernanceBody;
  label: string;
  note: string;
  tapeColor: 'sage' | 'terracotta' | 'ochre';
}

export const GOVERNANCE_BODIES: GovernanceBodyMeta[] = [
  {
    id: 'direcao',
    label: '— Direção',
    note: 'Conduz o dia-a-dia. Decide, assina, responde.',
    tapeColor: 'terracotta',
  },
  {
    id: 'conselho-fiscal',
    label: '— Conselho Fiscal',
    note: 'Acompanha as contas e os procedimentos. Guarda o caderno em ordem.',
    tapeColor: 'sage',
  },
  {
    id: 'mesa-da-assembleia',
    label: '— Mesa da Assembleia',
    note: 'Modera as assembleias gerais. Garante a voz dos sócios.',
    tapeColor: 'ochre',
  },
];

export const TEAM: TeamMember[] = [
  // ── Direção ──
  {
    id: 'maria-alexandra-flor-bastos',
    name: 'Maria Alexandra Flor Bastos',
    role: 'Presidente da Direção',
    body: 'direcao',
  },
  {
    id: 'ana-paula-castro',
    name: 'Ana Paula Castro',
    role: 'Tesoureira da Direção',
    body: 'direcao',
  },
  {
    id: 'susana-bizarro',
    name: 'Susana Bizarro',
    role: 'Secretária da Direção',
    body: 'direcao',
  },

  // ── Conselho Fiscal ──
  {
    id: 'isabel-maria-oliveira',
    name: 'Isabel Maria Oliveira',
    role: 'Presidente do Conselho Fiscal',
    body: 'conselho-fiscal',
  },
  {
    id: 'erica-bizarro',
    name: 'Erica Bizarro',
    role: 'Secretária do Conselho Fiscal',
    body: 'conselho-fiscal',
  },
  {
    id: 'goncalo-neves',
    name: 'Gonçalo Neves',
    role: 'Vogal do Conselho Fiscal',
    body: 'conselho-fiscal',
  },

  // ── Mesa da Assembleia ──
  {
    id: 'isabel-maria-rodrigues',
    name: 'Isabel Maria Rodrigues',
    role: 'Presidente da Mesa da Assembleia',
    body: 'mesa-da-assembleia',
  },
  {
    id: 'monica-abreu-dos-santos',
    name: 'Mónica Abreu dos Santos',
    role: '1ª Secretária da Mesa da Assembleia',
    body: 'mesa-da-assembleia',
  },
  {
    id: 'carla-filomena-neto',
    name: 'Carla Filomena Neto',
    role: '2ª Secretária da Mesa da Assembleia',
    body: 'mesa-da-assembleia',
  },
  {
    id: 'joana-filipa-santos',
    name: 'Joana Filipa Santos',
    role: '1ª Vogal da Mesa da Assembleia',
    body: 'mesa-da-assembleia',
  },
  {
    id: 'francisca-flor-damasceno',
    name: 'Francisca Flor Damasceno',
    role: '2ª Vogal da Mesa da Assembleia',
    body: 'mesa-da-assembleia',
  },
];

export function teamByBody(body: GovernanceBody): TeamMember[] {
  return TEAM.filter((m) => m.body === body);
}

/** Two-letter initials for the monogram polaroid. Strips diacritics. */
export function initialsFor(name: string): string {
  const tokens = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return '?';
  const first = tokens[0][0];
  const last = tokens.length > 1 ? tokens[tokens.length - 1][0] : '';
  return (first + last).toUpperCase();
}

// ── Colophon ─────────────────────────────────────────────────────────
//
// Contributors who built / continue to build the site. Distinct from
// TEAM above (formal governance). Lives at the bottom of /sobre under
// "— bastidores".

export interface ColophonEntry {
  id: string;
  name: string;
  role: string;
  note?: string;
}

export const COLOPHON: ColophonEntry[] = [
  {
    id: 'ricardo-pinho',
    name: 'Ricardo Pinho',
    role: 'Voluntário — tecnologia',
    note: 'desenvolvimento, infraestrutura, sítio público.',
  },
  {
    id: 'carlota-silva',
    name: 'Carlota Silva',
    role: 'Voluntária — animação',
    note: 'animação GIF.',
  },
];
